import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCurrencies } from '../hooks/useCurrencies';
import AuditPopover from '../components/AuditPopover';
import { TabIcon, TAB_ICONS } from '../utils/tabIcons';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import AttachmentManager from '../components/AttachmentManager';
import CustomFieldsSection from '../components/CustomFieldsSection';
import BarcodeScanner from '../components/BarcodeScanner';
import { fmtAmount, fmtQty, fmtRate } from '../utils/numberFormat';
import { Button, Modal, FormField, EmptyState, Pager, ListPageHeader, ListPageStats, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';
import BundleSection from '../components/BundleSection';

const IMG_BASE = window.location.hostname === 'localhost' ? 'http://localhost:5001' : '';

function StockThumb({ imageUrl, onClick }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  if (!imageUrl) return (
    <div className="stock-thumb-placeholder">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
    </div>
  );
  const handleMove = (e) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, left: Math.max(4, rect.left - 70) });
  };
  return (
    <div ref={wrapRef} className="stock-thumb-wrap" onMouseMove={handleMove} onClick={onClick}>
      <img src={IMG_BASE + imageUrl} alt="" onError={e => { e.target.style.display='none'; }} />
      <div className="thumb-zoom" style={{ top: pos.top, left: pos.left }}>
        <img src={IMG_BASE + imageUrl} alt="" />
      </div>
    </div>
  );
}

const TYPES = [
  { value: 'product', label: 'Mamul / Ticari Mal' },
  { value: 'raw', label: 'Hammadde' },
  { value: 'semi', label: 'Yari Mamul' },
  { value: 'service', label: 'Hizmet' },
  { value: 'expense', label: 'Sarf Malzeme' },
  { value: 'fixed', label: 'Demirbas' },
];

const VAT_RATES = [0, 1, 10, 20];
const COST_METHODS = [
  { value: 'average', label: 'Agirlikli Ortalama' },
  { value: 'fifo', label: 'FIFO' },
  { value: 'lifo', label: 'LIFO' },
];

// Stok kartı Fiziksel sekmesinde "grup gibi tanımlı" dropdown alanlar (stock_field_defs.field_key ↔ stok metin kolonu)
const STOCK_DEF_FIELDS = [
  { key: 'season',          label: 'Sezon',          col: 'season' },
  { key: 'year',            label: 'Yıl',            col: 'model_year' },
  { key: 'product_manager', label: 'Ürün Sorumlusu', col: 'product_manager' },
  { key: 'manufacturer',    label: 'Üretici Firma',  col: 'manufacturer' },
  { key: 'custom1',         label: 'Özel Kod 1',     col: 'custom_code1' },
  { key: 'custom2',         label: 'Özel Kod 2',     col: 'custom_code2' },
  { key: 'custom3',         label: 'Özel Kod 3',     col: 'custom_code3' },
  { key: 'custom4',         label: 'Özel Kod 4',     col: 'custom_code4' },
  { key: 'custom5',         label: 'Özel Kod 5',     col: 'custom_code5' },
  { key: 'origin',          label: 'Menşei Kodu',    col: 'origin_code' },
  { key: 'gtip',            label: 'GTİP Kodu',      col: 'gtip_code' },
  { key: 'cpa',             label: 'CPA Kodu',       col: 'cpa_code' },
];

const emptyStock = {
  code: '', name: '', full_name: '', type: 'product',
  product_code: '',
  category_id: '', brand_id: '', group_code: '', model: '',
  model_id: '', group_id: '',
  barcode: '', barcode2: '', barcode3: '', unit_id: '', unit2_id: '', unit3_id: '',
  unit2_ratio: '', unit3_ratio: '',
  sale_discount_rate: 0, purchase_discount_rate: 0,
  currency_code: 'TRY', vat_rate: 20, purchase_vat_rate: 20, withholding_rate: 0,
  track_inventory: true, track_serial: false, track_lot: false, track_expiry: false,
  allow_negative: false, is_weighted: false, costing_method: 'average',
  min_stock: 0, max_stock: 0, reorder_point: 0,
  weight_net: '', weight_gross: '', width: '', height: '', depth: '',
  shelf_code: '', shelf_id: '', notes: '', is_active: true, is_favorite: false,
  custom_code1: '', custom_code2: '', custom_code3: '', custom_code4: '', custom_code5: '',
  season: '', model_year: '', product_manager: '', manufacturer: '',
  production_date: '', is_domestic: false, project_id: '',
  origin_code: '', gtip_code: '', cpa_code: '', otv_code: '', otv_rate: '',
  withholding_code: '', avg_shelf_days: '',
  color_id: '', size_id: '',
  commission_rate_retail: '', commission_rate_wholesale: '',
  sell_in_store: true, sell_in_ecommerce: true,
};

function StocksPage({ autoOpenNew = false, onSaved = null, onNavigate = null }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currencies } = useCurrencies();
  const [stocks, setStocks] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [priceDefs, setPriceDefs] = useState([]);
  const [bulkPriceModal, setBulkPriceModal] = useState(null); // { ids, clearFn }
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({...emptyStock});
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  // Hangi alanı dolduracak — 'barcode' (varsayılan) | 'barcode2' | 'barcode3'
  const [barcodeScannerTarget, setBarcodeScannerTarget] = useState('barcode');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [lookups, setLookups] = useState({ categories: [], brands: [], units: [], colors: [], sizes: [], models: [], groups: [], shelves: [], projects: [], fieldDefs: {} });
  const [priceItems, setPriceItems] = useState([]); // [{ price_definition_id, name, price_group, def_currency_code, item_id, price1, price2, price3, vat_included, currency_code }]
  const [movements, setMovements] = useState([]);
  const [showQuickAdd, setShowQuickAdd] = useState(null);
  const [quickForm, setQuickForm] = useState({ code: '', name: '' });
  const [showModal, setShowModal] = useState(false);
  const [analysisStock, setAnalysisStock] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const imgInputRef = useRef(null);
  const [images, setImages] = useState([]); // [{id, image_url, display_order, is_primary}]
  const [trackColorSize, setTrackColorSize] = useState(false);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ type: '', category_id: '', brand_id: '', is_active: '', stock_state: '' });

  useEffect(() => {
    api.get('/settings').then(r => {
      if (r.data.success) setTrackColorSize(String(r.data.data?.color_size_tracking) === 'true');
    }).catch(() => {});
  }, []);

  const fetchStocks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, page, limit: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/stocks', { params });
      if (res.data.success) { setStocks(res.data.data.items); setTotal(res.data.data.total); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, page, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get('/stocks/stats');
      if (r.data.success) setStats(r.data.data);
    } catch {}
  }, []);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Price definitions — bulk fiyat aksiyonu için
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/price-definitions');
        if (r.data.success) setPriceDefs((r.data.data || []).filter(d => d.is_active !== false));
      } catch {}
    })();
  }, []);

  const fetchLookups = async () => {
    try {
      const res = await api.get('/stocks/lookups/all');
      if (res.data.success) setLookups(res.data.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchStocks(); }, [fetchStocks]);
  useEffect(() => { fetchLookups(); }, []);
  useEffect(() => {
    if (autoOpenNew) {
      setSelected(null); setForm({...emptyStock}); setIsEditing(true); setIsNew(true);
      setActiveTab('general'); setMsg(null); setShowModal(true);
    }
  }, [autoOpenNew]);

  // Yeni stoklar için boş fiyat tablosu (aktif tüm price_definitions için bir satır)
  const buildEmptyPriceItems = useCallback(async () => {
    try {
      const r = await api.get('/price-definitions');
      if (r.data.success) {
        return (r.data.data || []).filter(d => d.is_active !== false).map(d => ({
          price_definition_id: d.id,
          name: d.name, code: d.code, price_group: d.price_group,
          def_currency_code: d.currency_code, def_is_preferred: d.is_preferred,
          item_id: null,
          price1: 0, price2: '', price3: '',
          vat_included: false,
          currency_code: '', // null → definition'ı miras alır
        }));
      }
    } catch (e) { console.error('price defs fetch:', e); }
    return [];
  }, []);

  const fetchPrices = useCallback(async (stockId) => {
    try {
      const r = await api.get('/stocks/' + stockId + '/prices');
      if (r.data.success) {
        setPriceItems((r.data.data || []).map(d => ({
          price_definition_id: d.price_definition_id,
          name: d.name, code: d.code, price_group: d.price_group,
          def_currency_code: d.def_currency_code, def_is_preferred: d.def_is_preferred,
          item_id: d.item_id || null,
          price1: d.price1 != null ? d.price1 : 0,
          price2: d.price2 != null ? d.price2 : '',
          price3: d.price3 != null ? d.price3 : '',
          vat_included: !!d.vat_included,
          currency_code: d.item_currency_code || '',
        })));
      }
    } catch (e) { console.error('fetchPrices:', e); }
  }, []);

  const loadImages = useCallback(async (stockId) => {
    if (!stockId) { setImages([]); return; }
    try {
      const r = await api.get('/stocks/' + stockId + '/images');
      if (r.data?.success) setImages(r.data.data || []);
    } catch { setImages([]); }
  }, []);

  const handleSelect = async (stock) => {
    try {
      const res = await api.get('/stocks/' + stock.id);
      if (res.data.success) {
        setSelected(res.data.data);
        setForm(res.data.data);
        setIsEditing(false);
        setIsNew(false);
        setActiveTab('general');
        setMsg(null);
        setShowModal(true);
        loadMovements(stock.id);
        fetchPrices(stock.id);
        loadImages(stock.id);
      }
    } catch (err) { console.error(err); }
  };

  const handleNew = async () => {
    setSelected(null);
    setForm({...emptyStock});
    setImages([]);
    setIsEditing(true);
    setIsNew(true);
    setActiveTab('general');
    setMsg(null);
    setShowModal(true);
    setPriceItems(await buildEmptyPriceItems());
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelected(null);
    setForm({...emptyStock});
    setImages([]);
    setIsEditing(false);
    setIsNew(false);
    setMsg(null);
  };

  const handleEdit = () => { setIsEditing(true); setMsg(null); };
  const handleCancel = () => {
    if (isNew) { handleModalClose(); }
    else { setForm(selected); setIsEditing(false); setMsg(null); }
  };

  // mode: 'close' (default) | 'new' (kaydet ve yeni aç) | 'copy' (kaydet ve kopyala)
  const handleSave = async (mode = 'close') => {
    // Event objesi gelirse (eski API), 'close' kabul et
    if (mode && typeof mode === 'object') mode = 'close';
    const isService = form.type === 'service';
    if (!form.name) {
      setMsg({ type: 'error', text: 'Stok adı zorunludur (kod boş bırakılabilir, otomatik üretilir)' });
      return;
    }
    if (!isService && !form.unit_id) {
      setMsg({ type: 'error', text: 'Birim zorunludur' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const res = await api.post('/stocks', form);
        if (res.data.success) {
          const newId = res.data.data.id;
          // priceItems'i yeni stoğa kaydet
          try {
            const items = priceItems.map(p => ({
              price_definition_id: p.price_definition_id,
              price1: parseFloat(p.price1) || 0,
              price2: p.price2 === '' || p.price2 == null ? null : parseFloat(p.price2),
              price3: p.price3 === '' || p.price3 == null ? null : parseFloat(p.price3),
              vat_included: !!p.vat_included,
              currency_code: p.currency_code || null,
            }));
            if (items.length) await api.post('/stocks/' + newId + '/prices', { items });
          } catch (e) { console.error('save prices (new):', e); }

          setMsg({ type: 'success', text: 'Stok karti olusturuldu' });
          setIsNew(false); setIsEditing(false);
          fetchStocks();
          const detail = await api.get('/stocks/' + newId);
          if (detail.data.success) { setSelected(detail.data.data); setForm(detail.data.data); }
          fetchPrices(newId);
          if (onSaved) onSaved(detail.data?.data || res.data.data);
        }
      } else {
        // priceItems'ı stoğa kaydet (post). Eğer apply_to_siblings true ise kardeşlere de uygulanır.
        const savePrices = async (sid, applyToSibs) => {
          try {
            const items = priceItems
              .map(p => ({
                price_definition_id: p.price_definition_id,
                price1: parseFloat(p.price1) || 0,
                price2: p.price2 === '' || p.price2 == null ? null : parseFloat(p.price2),
                price3: p.price3 === '' || p.price3 == null ? null : parseFloat(p.price3),
                vat_included: !!p.vat_included,
                currency_code: p.currency_code || null,
              }));
            await api.post('/stocks/' + sid + '/prices', { items, apply_to_siblings: !!applyToSibs });
          } catch (e) { console.error('save prices:', e); }
        };

        // Aynı ürün kodundan başka stok var mı? Varsa kullanıcıya sor
        let applyToSiblings = false;
        if (form.product_code && String(form.product_code).trim()) {
          try {
            const sib = await api.get('/stocks/' + selected.id + '/siblings-count');
            if (sib.data.success && sib.data.data.sibling_count > 0) {
              const cnt = sib.data.data.sibling_count;
              applyToSiblings = window.confirm(
                `Aynı ürün kodundan (${form.product_code}) ${cnt} stok daha var.\n\n` +
                `Onlarda da bu değişiklikleri uygulamak ister misiniz?\n` +
                `(stok kodu, renk, beden, barkod ve resim her stoğun kendisinde kalır)\n\n` +
                `Evet (OK) → ${cnt} stok ile birlikte güncelle\nHayır (Cancel) → sadece bu stoğu güncelle`
              );
            }
          } catch (e) { console.error('siblings-count:', e); }
        }
        const body = applyToSiblings ? { ...form, apply_to_siblings: true } : form;
        const res = await api.put('/stocks/' + selected.id, body);
        if (res.data.success) {
          await savePrices(selected.id, applyToSiblings);
          const sibCount = res.data.updated_siblings || 0;
          setMsg({ type: 'success', text: sibCount > 0
            ? `Stok karti guncellendi (+${sibCount} kardeş stok)`
            : 'Stok karti guncellendi' });
          setIsEditing(false);
          fetchStocks();
          const detail = await api.get('/stocks/' + selected.id);
          if (detail.data.success) { setSelected(detail.data.data); setForm(detail.data.data); }
          fetchPrices(selected.id);
        }
      }
      // Post-save mode handling
      if (mode === 'close') {
        setTimeout(() => handleModalClose(), 250);
      } else if (mode === 'new') {
        // Yeni boş kart aç
        setTimeout(() => {
          setSelected(null); setForm({ ...emptyStock }); setPriceItems([]); setMovements([]);
          setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null);
        }, 250);
      } else if (mode === 'copy') {
        // Mevcut form'u kopyala — kod ve barkod sıfırlansın, yeni kayıt olarak ekle
        setTimeout(() => {
          const copy = { ...form, id: undefined, code: '', barcode: '', barcode2: '', barcode3: '' };
          setSelected(null); setForm(copy); setPriceItems(priceItems.map(p => ({ ...p, item_id: undefined })));
          setMovements([]); setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null);
        }, 250);
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Hata olustu' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Bu stok kartını silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete('/stocks/' + selected.id);
      handleModalClose(); fetchStocks();
    } catch(e) {
      if (e.response?.status === 409 && e.response.data?.hasMovements) {
        setMsg({ type: 'error', text: e.response.data.message || 'Önce bu stok kartına bağlı hareketleri silin' });
      } else {
        setMsg({ type: 'error', text: e.response?.data?.message || 'Silme hatası' });
      }
    }
  };

  const handleQuickAdd = async () => {
    if (!quickForm.name) return;
    try {
      const endpoint = showQuickAdd === 'category' ? '/stocks/categories' :
                       showQuickAdd === 'brand' ? '/stocks/brands' :
                       showQuickAdd === 'color' ? '/colors' :
                       showQuickAdd === 'size' ? '/sizes' :
                       showQuickAdd === 'model' ? '/stocks/models' :
                       showQuickAdd === 'group' ? '/stocks/groups' :
                       showQuickAdd === 'shelf' ? '/stocks/shelves' : '/stocks/units';
      await api.post(endpoint, quickForm);
      fetchLookups();
      setShowQuickAdd(null);
      setQuickForm({ code: '', name: '' });
    } catch (err) { alert('Ekleme hatasi: ' + (err.response?.data?.message || err.message)); }
  };

  const loadMovements = async (id) => {
    try {
      const res = await api.get('/stocks/' + id + '/movements');
      if (res.data.success) setMovements(res.data.data);
    } catch (err) { console.error(err); }
  };

  const f = (key, val) => setForm(prev => {
    const next = { ...prev, [key]: val };
    // Hizmet seçilince stok takibi anlamsız — otomatik kapat
    if (key === 'type' && val === 'service') {
      next.track_inventory = false;
      next.track_serial = false;
      next.track_lot = false;
      next.track_expiry = false;
      next.allow_negative = true;
      next.min_stock = 0;
      next.max_stock = 0;
      next.reorder_point = 0;
    }
    // Hizmet'ten başka bir tipe geçilince takibi tekrar aç
    if (key === 'type' && prev.type === 'service' && val !== 'service') {
      next.track_inventory = true;
    }
    return next;
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selected?.id) return;
    if (images.length >= 4) {
      setMsg({ type: 'error', text: 'En fazla 4 resim yüklenebilir' });
      if (imgInputRef.current) imgInputRef.current.value = '';
      return;
    }
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/stocks/' + selected.id + '/images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        await loadImages(selected.id);
        // Primary değiştiyse listede thumbnail güncellensin
        if (res.data.data?.is_primary) {
          const updated = { ...selected, image_url: res.data.data.image_url };
          setSelected(updated); setForm(updated);
        }
        fetchStocks();
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Resim yüklenemedi' });
    } finally {
      setImgUploading(false);
      if (imgInputRef.current) imgInputRef.current.value = '';
    }
  };

  const handleImageDelete = async (imageId) => {
    if (!selected?.id || !imageId) return;
    if (!window.confirm('Resmi silmek istiyor musunuz?')) return;
    try {
      await api.delete('/stocks/' + selected.id + '/images/' + imageId);
      await loadImages(selected.id);
      // Backend primary'yi otomatik yeniden seçer; selected.image_url'yi de güncelleyelim
      const detail = await api.get('/stocks/' + selected.id);
      if (detail.data?.success) { setSelected(detail.data.data); setForm(detail.data.data); }
      fetchStocks();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Resim silinemedi' });
    }
  };

  const handleImageSetPrimary = async (imageId) => {
    if (!selected?.id || !imageId) return;
    try {
      await api.post('/stocks/' + selected.id + '/images/' + imageId + '/primary');
      await loadImages(selected.id);
      const detail = await api.get('/stocks/' + selected.id);
      if (detail.data?.success) { setSelected(detail.data.data); setForm(detail.data.data); }
      fetchStocks();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Ana resim ayarlanamadı' });
    }
  };

  const tabs = [
    { key: 'general',   label: t('tabs.general'),    icon: 'general' },
    { key: 'pricing',   label: t('tabs.pricing'),    icon: 'pricing' },
    { key: 'units',     label: t('tabs.units'),      icon: 'units' },
    { key: 'bundle',    label: 'Paket / Bundle',     icon: 'package' },
    { key: 'tracking',  label: t('tabs.tracking'),   icon: 'tracking' },
    { key: 'physical',  label: t('tabs.physical'),   icon: 'ruler' },
    { key: 'movements', label: t('tabs.movements'),  icon: 'activity' },
    { key: 'image',     label: t('tabs.image'),      icon: 'image' },
    { key: 'custom',    label: t('tabs.custom'),     icon: 'sparkles' },
    { key: 'documents', label: t('tabs.documents'),  icon: 'paperclip' },
    { key: 'notes',     label: t('tabs.notes'),      icon: 'notes' },
  ];

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inEdit = isEditing;
  const inView = !isEditing && !isNew;

  // Form grid responsive layout
  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Stok Kartları"
        count={total}
        unit="kayit"
        search={search}
        onSearch={v => { setSearch(v); setPage(1); }}
        searchPlaceholder="Kod, ad veya barkod ara..."
        onRefresh={() => { fetchStocks(); fetchStats(); }}
        refreshing={loading}
        actions={<Button size="sm" onClick={handleNew}>+ Yeni Stok</Button>}
      />

      <ListPageStats
        loading={stats == null}
        stats={[
          {
            key: 'total',
            label: 'Toplam Aktif Ürün',
            value: stats?.total,
            delta: stats?.new_30d > 0 ? stats.new_30d : null,
            deltaTooltip: 'Son 30 günde eklenen',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>,
            onClick: () => { setFilters(f => ({ ...f, stock_state: '', is_active: 'yes' })); setPage(1); },
          },
          {
            key: 'critical',
            label: 'Kritik Seviye',
            value: stats?.critical,
            color: 'warning',
            sublabel: 'min. stoğun altı',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
            onClick: () => { setFilters(f => ({ ...f, stock_state: 'critical' })); setPage(1); },
          },
          {
            key: 'out',
            label: 'Tükenmiş',
            value: stats?.out_of_stock,
            color: 'danger',
            sublabel: 'elde 0 veya altı',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
            onClick: () => { setFilters(f => ({ ...f, stock_state: 'out' })); setPage(1); },
          },
          {
            key: 'inactive',
            label: 'Pasif Kartlar',
            value: stats?.inactive,
            color: 'neutral',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/></svg>,
            onClick: () => { setFilters(f => ({ ...f, is_active: 'no' })); setPage(1); },
          },
        ]}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={v => { setFilters(v); setPage(1); }}
        fields={[
          { key: 'type', type: 'select', label: 'Tip',
            options: [{ value: '', label: 'Tümü' }, ...TYPES.map(t => ({ value: t.value, label: t.label }))] },
          { key: 'category_id', type: 'select', label: 'Kategori', flex: '1 1 200px', minWidth: 180,
            options: [{ value: '', label: 'Tümü' }, ...(lookups.categories || []).map(c => ({ value: c.id, label: c.name }))] },
          { key: 'brand_id', type: 'select', label: 'Marka', flex: '1 1 200px', minWidth: 180,
            options: [{ value: '', label: 'Tümü' }, ...(lookups.brands || []).map(b => ({ value: b.id, label: b.name }))] },
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
          { key: 'stock_state', type: 'select', label: 'Stok Durumu',
            options: [
              { value: '', label: 'Tümü' },
              { value: 'critical', label: 'Kritik Altı' },
              { value: 'out', label: 'Tükendi' },
              { value: 'available', label: 'Mevcut' },
            ] },
        ]}
      />

      {/* TABLO */}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey={trackColorSize ? 'stocks-list-cs' : 'stocks-list'}
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Stok bulunamadi"
          data={stocks}
          rowKey="id"
          onRowClick={s => handleSelect(s)}
          bulkActions={[
            {
              key: 'activate',
              label: 'Aktif Yap',
              variant: 'ghost',
              onClick: async (ids, clear) => {
                try {
                  const r = await api.post('/stocks/bulk', { action: 'set_active', ids });
                  if (r.data.success) { clear(); fetchStocks(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'deactivate',
              label: 'Pasif Yap',
              variant: 'ghost',
              confirm: 'Seçili stok kartları pasif yapılacak. Emin misiniz?',
              onClick: async (ids, clear) => {
                try {
                  const r = await api.post('/stocks/bulk', { action: 'set_inactive', ids });
                  if (r.data.success) { clear(); fetchStocks(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_category',
              label: 'Kategori Ata',
              variant: 'primary',
              prompt: {
                type: 'select',
                title: 'Toplu Kategori Atama',
                description: 'Seçili stoklara atanacak kategoriyi seçin:',
                label: 'Kategori',
                options: (lookups.categories || []).map(c => ({ value: c.id, label: c.name })),
                allowEmpty: true,
                emptyLabel: '— Kategoriyi kaldır —',
                submitLabel: 'Kategoriye Ata',
              },
              onClick: async (ids, clear, value) => {
                try {
                  const r = await api.post('/stocks/bulk', { action: 'set_category', ids, value });
                  if (r.data.success) { clear(); fetchStocks(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_brand',
              label: 'Marka Ata',
              variant: 'primary',
              prompt: {
                type: 'select',
                title: 'Toplu Marka Atama',
                description: 'Seçili stoklara atanacak markayı seçin:',
                label: 'Marka',
                options: (lookups.brands || []).map(b => ({ value: b.id, label: b.name })),
                allowEmpty: true,
                emptyLabel: '— Markayı kaldır —',
                submitLabel: 'Markaya Ata',
              },
              onClick: async (ids, clear, value) => {
                try {
                  const r = await api.post('/stocks/bulk', { action: 'set_brand', ids, value });
                  if (r.data.success) { clear(); fetchStocks(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_prices',
              label: 'Fiyat Güncelle',
              variant: 'primary',
              onClick: async (ids, clear) => {
                // Custom modal aç (DataTable prompt'u yetersiz — multi-cell tablo)
                setBulkPriceModal({ ids, clearFn: clear });
              },
            },
            {
              key: 'delete',
              label: 'Sil',
              variant: 'danger',
              confirm: 'Seçili stok kartları SİLİNECEK. Hareketi olan kartlar silinemez (pasif yap önerilir). Devam edilsin mi?',
              onClick: async (ids, clear) => {
                try {
                  const r = await api.post('/stocks/bulk', { action: 'delete', ids });
                  if (r.data.success) { clear(); fetchStocks(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
          ]}
          columns={[
            { id: 'thumb', header: '', sortable: false, hideable: false, draggable: false, width: 48,
              headerClassName: 'stock-thumb-cell', className: 'stock-thumb-cell',
              cell: s => (
                <span onClick={e => e.stopPropagation()}>
                  <StockThumb imageUrl={s.image_url} onClick={() => handleSelect(s)} />
                </span>
              ) },
            { id: 'code', header: 'Stok Kodu', accessor: 'code', sortable: true,
              cell: s => <span className="stock-code">{s.code}</span> },
            { id: 'name', header: 'Stok Adi', accessor: 'name', sortable: true,
              className: 'stock-name-cell' },
            { id: 'type', header: 'Tip', accessor: s => TYPES.find(t => t.value === s.type)?.label || s.type, sortable: true,
              cell: s => <span className="stock-badge active" style={{background:'rgba(59,130,246,0.1)',color:'var(--info)'}}>{TYPES.find(t => t.value === s.type)?.label || s.type}</span> },
            { id: 'category', header: 'Kategori', accessor: 'category_name', sortable: true,
              className: 'stock-secondary', cell: s => s.category_name || '-' },
            { id: 'brand', header: 'Marka', accessor: 'brand_name', sortable: true,
              className: 'stock-secondary', cell: s => s.brand_name || '-' },
            ...(trackColorSize ? [
              { id: 'color', header: 'Renk', accessor: 'color_name', sortable: true,
                className: 'stock-secondary', cell: s => s.color_name || '-' },
              { id: 'size', header: 'Beden', accessor: 'size_name', sortable: true,
                className: 'stock-secondary', cell: s => s.size_name || '-' },
            ] : []),
            { id: 'on_hand', header: 'Mevcut', accessor: s => parseFloat(s.on_hand_qty) || 0, sortable: true, width: 120,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: s => (
                <span style={{ fontFamily: 'monospace', fontSize: '0.84rem' }}>
                  {fmtQty(s.on_hand_qty || 0)}
                  {s.unit_code && <span style={{ color: 'var(--text-tertiary)', marginLeft: 4, fontSize: '0.75rem' }}>{s.unit_code}</span>}
                </span>
              ) },
            { id: 'reserved', header: 'Rezerv', accessor: s => parseFloat(s.reserved_qty) || 0, sortable: true, width: 120,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: s => {
                const r = parseFloat(s.reserved_qty) || 0;
                return r > 0
                  ? <span style={{ fontFamily: 'monospace', fontSize: '0.84rem', color: '#b45309', fontWeight: 600 }} title="Pending onay belgelerinde tutulan miktar">
                      {fmtQty(r)}
                      {s.unit_code && <span style={{ color: '#b4530999', marginLeft: 4, fontSize: '0.75rem', fontWeight: 500 }}>{s.unit_code}</span>}
                    </span>
                  : <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
              } },
            { id: 'available', header: 'Kullanılabilir', accessor: s => (parseFloat(s.on_hand_qty) || 0) - (parseFloat(s.reserved_qty) || 0), sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: s => {
                const av = (parseFloat(s.on_hand_qty) || 0) - (parseFloat(s.reserved_qty) || 0);
                return (
                  <strong style={{ fontFamily: 'monospace', fontSize: '0.86rem', color: av <= 0 ? 'var(--danger)' : 'var(--success)' }}>
                    {fmtQty(av)}
                    {s.unit_code && <span style={{ marginLeft: 4, fontSize: '0.75rem', fontWeight: 500, opacity: 0.75 }}>{s.unit_code}</span>}
                  </strong>
                );
              } },
            { id: 'status', header: 'Durum', accessor: s => s.is_active ? 1 : 0, sortable: true,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: s => <span className={'stock-badge ' + (s.is_active ? 'active' : 'passive')}>{s.is_active ? 'Aktif' : 'Pasif'}</span> },
            { id: 'analysis', header: '', sortable: false, hideable: false, draggable: false, width: 70,
              cellStyle: { textAlign: 'center' },
              cell: s => (
                <Button variant="ghost" size="sm" onClick={async e => {
                  e.stopPropagation();
                  setAnalysisStock(s); setAnalysisData(null); setAnalysisLoading(true);
                  try { const r = await api.get('/stocks/'+s.id+'/analysis'); if (r.data.success) setAnalysisData(r.data.data); }
                  catch(ex){} finally { setAnalysisLoading(false); }
                }}>Analiz</Button>
              ) },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: s => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/stocks/${s.id}/audit`} /></span> },
          ]}
        />
      </div>

      <Pager page={page} totalPages={Math.ceil(total/50)} onChange={setPage} />

      {/* DETAY MODAL */}
      {showModal && (
        <Modal
          icon={null}
          title={isNew ? 'Yeni Stok Kartı' : 'Stok Kartı'}
          size="xl"
          onClose={handleModalClose}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && (
                  <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEdit && (
                  <Button variant="ghost" onClick={handleCancel}>
                    {isNew ? 'Vazgeç' : 'İptal'}
                  </Button>
                )}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç',  icon: '✚',  onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kopyala', icon: '⎘',  onClick: () => handleSave('copy') },
                      { label: 'Kaydet ve Kapat',   icon: '✓',  onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && (
                  <Button onClick={handleEdit}>✎ Düzenle</Button>
                )}
              </div>
            </>
          }
        >
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          {/* HERO STRIP — yeni kart tasarımı */}
          {!isNew && (
            <CardHero
              icon="📦"
              code={form.code}
              title={form.name}
              subtitle={[
                TYPES.find(t => t.value === form.type)?.label,
                lookups.categories.find(c => c.id === form.category_id)?.name,
                lookups.brands.find(b => b.id === form.brand_id)?.name,
                form.barcode && ('Barkod: ' + form.barcode),
              ].filter(Boolean).join(' · ')}
              statusBadge={<CardStatusBadge preset={form.is_active ? 'active' : 'inactive'} />}
              kpis={[
                {
                  label: 'Toplam Stok',
                  value: fmtQty(form.on_hand_qty || 0),
                  color: (form.on_hand_qty || 0) <= 0 ? 'danger'
                       : (form.min_stock > 0 && form.on_hand_qty < form.min_stock) ? 'warning'
                       : 'success',
                  sublabel: form.warehouses_with_stock ? `${form.warehouses_with_stock} depoda` : (form.unit_code || form.unit_name || 'adet'),
                },
                priceItems.length > 0 && { label: 'Fiyat Listeleri', value: priceItems.length, color: 'primary', sublabel: 'tanımlı' },
                movements.length > 0 && { label: 'Hareketler', value: movements.length, color: 'info', sublabel: 'son kayıtlar' },
                form.min_stock > 0 && { label: 'Min. Stok', value: fmtQty(form.min_stock), color: 'warning' },
                form.track_lot && { label: 'Lot Takip', value: '●', color: 'success' },
                form.track_serial && { label: 'Seri Takip', value: '●', color: 'success' },
              ].filter(Boolean)}
            />
          )}

          {/* TABLAR */}
          <CardTabs
            items={tabs.map(t => ({ key: t.key, label: t.label, icon: <TabIcon name={t.icon} /> }))}
            active={activeTab}
            onChange={setActiveTab}
          />

          {activeTab === 'general' && (
            <div style={gridStyle}>
              <FormField label="Stok Kodu" required>
                <input value={form.code || ''} onChange={e => f('code', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Stok Adi" required>
                <input value={form.name || ''} onChange={e => f('name', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Ürün Kodu" hint="renk/beden varyantlarını aynı ürünle gruplar">
                <input value={form.product_code || ''} onChange={e => f('product_code', e.target.value)} disabled={!isEditing} placeholder="Örn: TSH-001" />
              </FormField>
              <FormField label="Uzun Ad / Açıklama" fullWidth>
                <input value={form.full_name || ''} onChange={e => f('full_name', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Tip">
                <select value={form.type || 'product'} onChange={e => f('type', e.target.value)} disabled={!isEditing}>
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </FormField>
              <FormField label={<>Kategori <button className="quick-add-btn" onClick={() => setShowQuickAdd('category-mgr')}>+</button></>}>
                <select value={form.category_id || ''} onChange={e => f('category_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {lookups.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FormField>
              <FormField label={<>Marka <button className="quick-add-btn" onClick={() => setShowQuickAdd('brand-mgr')}>+</button></>}>
                <select value={form.brand_id || ''} onChange={e => f('brand_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {lookups.brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </FormField>
              <FormField label={<>Barkod {isEditing && <>
                <button type="button" className="quick-add-btn" title="Otomatik barkod üret" onClick={async () => {
                  try {
                    const r = await api.post('/stocks/next-barcode');
                    if (r.data.success) f('barcode', r.data.data.barcode);
                  } catch (e) { alert('Barkod üretilemedi: ' + (e.response?.data?.message || e.message)); }
                }}>+</button>
                <button type="button" className="quick-add-btn" title="Kamera ile barkod tara" onClick={() => { setBarcodeScannerTarget('barcode'); setBarcodeScannerOpen(true); }} style={{ marginLeft: 4 }}>📷</button>
              </>}</>}>
                <input value={form.barcode || ''} onChange={e => f('barcode', e.target.value)} disabled={!isEditing} placeholder={isEditing ? 'Yazın, + ile üretin veya 📷 ile tarayın' : ''} />
              </FormField>
              <FormField label={<>Model <button className="quick-add-btn" onClick={() => setShowQuickAdd('model-mgr')}>+</button></>}>
                <select value={form.model_id || ''} onChange={e => f('model_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {(lookups.models || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </FormField>
              <FormField label={<>Grup <button className="quick-add-btn" onClick={() => setShowQuickAdd('group-mgr')}>+</button></>}>
                <select value={form.group_id || ''} onChange={e => f('group_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {(lookups.groups || []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </FormField>
              <FormField label="Durum">
                <select value={form.is_active ? '1' : '0'} onChange={e => f('is_active', e.target.value === '1')} disabled={!isEditing}>
                  <option value="1">Aktif</option>
                  <option value="0">Pasif</option>
                </select>
              </FormField>
              <FormField label="POS Favorisi" hint="Perakende satışta renkli butonda görünür">
                <select value={form.is_favorite ? '1' : '0'} onChange={e => f('is_favorite', e.target.value === '1')} disabled={!isEditing}>
                  <option value="0">Hayır</option>
                  <option value="1">⭐ Favori</option>
                </select>
              </FormField>
              <FormField label="Mağazada Satılır">
                <select value={form.sell_in_store ? '1' : '0'} onChange={e => f('sell_in_store', e.target.value === '1')} disabled={!isEditing}>
                  <option value="1">Evet</option>
                  <option value="0">Hayır</option>
                </select>
              </FormField>
              <FormField label="E-Ticarette Satılır">
                <select value={form.sell_in_ecommerce ? '1' : '0'} onChange={e => f('sell_in_ecommerce', e.target.value === '1')} disabled={!isEditing}>
                  <option value="1">Evet</option>
                  <option value="0">Hayır</option>
                </select>
              </FormField>
            </div>
          )}

          {activeTab === 'pricing' && (
            <div>
              <div style={{fontWeight:600, fontSize:'0.85rem', color:'var(--text-secondary)', marginBottom:8}}>Fiyatlar — Tanımlanmış fiyat türlerine göre</div>
              {priceItems.length === 0 ? (
                <EmptyState
                  compact
                  title="Fiyat tanımı yok"
                  description="Program Tanımları → Fiyat Tanımları'ndan ekleyin."
                />
              ) : (
                <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch', border:'1px solid var(--border-secondary)', borderRadius:8}}>
                  <table style={{width:'100%', minWidth:640, borderCollapse:'collapse', fontSize:'0.83rem'}}>
                    <thead style={{background:'var(--bg-secondary)'}}>
                      <tr>
                        <th style={{padding:'8px 10px', textAlign:'left'}}>Açıklama</th>
                        <th style={{padding:'8px 8px', textAlign:'right', width:120}}>Fiyat-1</th>
                        <th style={{padding:'8px 8px', textAlign:'right', width:120}}>Fiyat-2</th>
                        <th style={{padding:'8px 8px', textAlign:'right', width:120}}>Fiyat-3</th>
                        <th style={{padding:'8px 8px', textAlign:'center', width:90}}>Para Birimi</th>
                        <th style={{padding:'8px 8px', textAlign:'center', width:60}}>KDV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceItems.map((p, idx) => (
                        <tr key={p.price_definition_id} style={{borderTop:'1px solid var(--border-secondary)', background: p.def_is_preferred ? 'rgba(99,102,241,0.04)' : 'transparent'}}>
                          <td style={{padding:'7px 10px'}}>
                            <div style={{fontWeight:600}}>{p.name} {p.def_is_preferred && <span title="Tercih edilen" style={{color:'#f59e0b'}}>★</span>}</div>
                            <div style={{fontSize:'0.7rem', color:'var(--text-tertiary)'}}>{p.price_group || '—'}</div>
                          </td>
                          <td style={{padding:'4px'}}>
                            <input type="number" step="0.0001" value={p.price1}
                              disabled={!isEditing}
                              onChange={e => setPriceItems(prev => prev.map((x, i) => i === idx ? { ...x, price1: e.target.value } : x))}
                              style={{width:'100%', textAlign:'right', fontFamily:'monospace'}}/>
                          </td>
                          <td style={{padding:'4px'}}>
                            <input type="number" step="0.0001" value={p.price2}
                              disabled={!isEditing}
                              onChange={e => setPriceItems(prev => prev.map((x, i) => i === idx ? { ...x, price2: e.target.value } : x))}
                              placeholder={isEditing ? '— üstü çizili —' : ''}
                              style={{width:'100%', textAlign:'right', fontFamily:'monospace'}}/>
                          </td>
                          <td style={{padding:'4px'}}>
                            <input type="number" step="0.0001" value={p.price3}
                              disabled={!isEditing}
                              onChange={e => setPriceItems(prev => prev.map((x, i) => i === idx ? { ...x, price3: e.target.value } : x))}
                              placeholder={isEditing ? '— vadeli/kart —' : ''}
                              style={{width:'100%', textAlign:'right', fontFamily:'monospace'}}/>
                          </td>
                          <td style={{padding:'4px'}}>
                            <select disabled={!isEditing} value={p.currency_code}
                              onChange={e => setPriceItems(prev => prev.map((x, i) => i === idx ? { ...x, currency_code: e.target.value } : x))}
                              style={{width:'100%', fontSize:'0.78rem'}}
                              title={`Boş = tanımdan miras (${p.def_currency_code})`}>
                              <option value="">{p.def_currency_code}</option>
                              {currencies.map(c => <option key={c.id||c.code} value={c.code}>{c.code}</option>)}
                            </select>
                          </td>
                          <td style={{padding:'4px', textAlign:'center'}}>
                            <input type="checkbox" checked={!!p.vat_included} disabled={!isEditing}
                              onChange={e => setPriceItems(prev => prev.map((x, i) => i === idx ? { ...x, vat_included: e.target.checked } : x))}/>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{marginTop:16, paddingTop:12, borderTop:'1px dashed var(--border-secondary)', fontWeight:600, fontSize:'0.85rem', color:'var(--text-secondary)', marginBottom:8}}>Vergi & İskonto</div>
              <div style={gridStyle}>
                <FormField label="Alış KDV Oranı (%)">
                  <select value={form.purchase_vat_rate ?? form.vat_rate ?? 20} onChange={e => f('purchase_vat_rate', parseFloat(e.target.value))} disabled={!isEditing}>
                    {VAT_RATES.map(v => <option key={v} value={v}>%{v}</option>)}
                  </select>
                </FormField>
                <FormField label="Satış KDV Oranı (%)">
                  <select value={form.vat_rate ?? 20} onChange={e => f('vat_rate', parseFloat(e.target.value))} disabled={!isEditing}>
                    {VAT_RATES.map(v => <option key={v} value={v}>%{v}</option>)}
                  </select>
                </FormField>
                <FormField label="Tevkifat Orani (%)">
                  <input type="number" step="0.01" value={form.withholding_rate || 0} onChange={e => f('withholding_rate', parseFloat(e.target.value) || 0)} disabled={!isEditing} />
                </FormField>
                <FormField label="Satış Iskonto Orani (%)">
                  <input type="number" step="0.01" value={form.sale_discount_rate || 0} onChange={e => f('sale_discount_rate', parseFloat(e.target.value) || 0)} disabled={!isEditing} />
                </FormField>
                <FormField label="Alis Iskonto Orani (%)">
                  <input type="number" step="0.01" value={form.purchase_discount_rate || 0} onChange={e => f('purchase_discount_rate', parseFloat(e.target.value) || 0)} disabled={!isEditing} />
                </FormField>
              </div>
              <div style={{marginTop:16, paddingTop:12, borderTop:'1px dashed var(--border-secondary)', fontWeight:600, fontSize:'0.85rem', color:'var(--text-secondary)', marginBottom:8}}>Prim Oranları (Satış Temsilcisine)</div>
              <div style={gridStyle}>
                <FormField label="Perakende Prim (%)">
                  <input type="number" step="0.01" min={0} max={100} value={form.commission_rate_retail||''} onChange={e=>f('commission_rate_retail',e.target.value)} disabled={!isEditing} placeholder="Stoğa özel; boşsa personel varsayılanı"/>
                </FormField>
                <FormField label="Toptan Prim (%)">
                  <input type="number" step="0.01" min={0} max={100} value={form.commission_rate_wholesale||''} onChange={e=>f('commission_rate_wholesale',e.target.value)} disabled={!isEditing} placeholder="Fiyat tipi 2/3/4 ise kullanılır"/>
                </FormField>
              </div>
            </div>
          )}

          {activeTab === 'units' && (
            <div style={gridStyle}>
              <FormField label={<>Ana Birim <button className="quick-add-btn" onClick={() => setShowQuickAdd('unit-mgr')}>+</button></>} required>
                <select value={form.unit_id || ''} onChange={e => f('unit_id', e.target.value)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {lookups.units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
                </select>
              </FormField>
              <div></div>
              <FormField label="2. Birim">
                <select value={form.unit2_id || ''} onChange={e => f('unit2_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {lookups.units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
                </select>
              </FormField>
              <FormField label="2. Birim Orani (1 birim = ? adet)">
                <input type="number" step="0.001" value={form.unit2_ratio || ''} onChange={e => f('unit2_ratio', parseFloat(e.target.value) || null)} disabled={!isEditing} />
              </FormField>
              <FormField label={<>2. Birim Barkod {isEditing && form.unit2_id && <>
                <button type="button" className="quick-add-btn" title="Otomatik barkod üret" onClick={async () => {
                  try {
                    const r = await api.post('/stocks/next-barcode');
                    if (r.data.success) f('barcode2', r.data.data.barcode);
                  } catch (e) { alert('Barkod üretilemedi: ' + (e.response?.data?.message || e.message)); }
                }}>+</button>
                <button type="button" className="quick-add-btn" title="Kamera ile barkod tara" onClick={() => { setBarcodeScannerTarget('barcode2'); setBarcodeScannerOpen(true); }} style={{ marginLeft: 4 }}>📷</button>
              </>}</>} hint="opsiyonel — 2. birim için ayrı barkod">
                <input value={form.barcode2 || ''} onChange={e => f('barcode2', e.target.value)} disabled={!isEditing || !form.unit2_id} placeholder={form.unit2_id ? '' : 'önce 2. birimi seçin'} />
              </FormField>
              <FormField label="3. Birim">
                <select value={form.unit3_id || ''} onChange={e => f('unit3_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {lookups.units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
                </select>
              </FormField>
              <FormField label="3. Birim Orani (1 birim = ? adet)">
                <input type="number" step="0.001" value={form.unit3_ratio || ''} onChange={e => f('unit3_ratio', parseFloat(e.target.value) || null)} disabled={!isEditing} />
              </FormField>
              <FormField label={<>3. Birim Barkod {isEditing && form.unit3_id && <>
                <button type="button" className="quick-add-btn" title="Otomatik barkod üret" onClick={async () => {
                  try {
                    const r = await api.post('/stocks/next-barcode');
                    if (r.data.success) f('barcode3', r.data.data.barcode);
                  } catch (e) { alert('Barkod üretilemedi: ' + (e.response?.data?.message || e.message)); }
                }}>+</button>
                <button type="button" className="quick-add-btn" title="Kamera ile barkod tara" onClick={() => { setBarcodeScannerTarget('barcode3'); setBarcodeScannerOpen(true); }} style={{ marginLeft: 4 }}>📷</button>
              </>}</>} hint="opsiyonel — 3. birim için ayrı barkod">
                <input value={form.barcode3 || ''} onChange={e => f('barcode3', e.target.value)} disabled={!isEditing || !form.unit3_id} placeholder={form.unit3_id ? '' : 'önce 3. birimi seçin'} />
              </FormField>
            </div>
          )}

          {activeTab === 'bundle' && (
            <BundleSection stockId={selected?.id} isNew={isNew} isEditing={isEditing} />
          )}

          {activeTab === 'tracking' && (
            <div style={gridStyle}>
              <FormField label="Maliyet Yontemi">
                <select value={form.costing_method || 'average'} onChange={e => f('costing_method', e.target.value)} disabled={!isEditing}>
                  {COST_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </FormField>
              <FormField label="Min Stok">
                <input type="number" step="0.001" value={form.min_stock || 0} onChange={e => f('min_stock', parseFloat(e.target.value) || 0)} disabled={!isEditing} />
              </FormField>
              <FormField label="Max Stok">
                <input type="number" step="0.001" value={form.max_stock || 0} onChange={e => f('max_stock', parseFloat(e.target.value) || 0)} disabled={!isEditing} />
              </FormField>
              <FormField label="Yeniden Siparis Noktasi">
                <input type="number" step="0.001" value={form.reorder_point || 0} onChange={e => f('reorder_point', parseFloat(e.target.value) || 0)} disabled={!isEditing} />
              </FormField>
              <FormField label="">
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.85rem'}}>
                  <input type="checkbox" checked={!!form.track_inventory} onChange={e => f('track_inventory', e.target.checked)} disabled={!isEditing} /> Stok Takibi
                </label>
              </FormField>
              <FormField label="">
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.85rem'}}>
                  <input type="checkbox" checked={!!form.track_serial} onChange={e => f('track_serial', e.target.checked)} disabled={!isEditing} /> Seri No Takibi
                </label>
              </FormField>
              <FormField label="">
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.85rem'}}>
                  <input type="checkbox" checked={!!form.track_lot} onChange={e => f('track_lot', e.target.checked)} disabled={!isEditing} /> Lot/Parti Takibi
                </label>
              </FormField>
              <FormField label="">
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.85rem'}}>
                  <input type="checkbox" checked={!!form.track_expiry} onChange={e => f('track_expiry', e.target.checked)} disabled={!isEditing} /> Son Kullanma Tarihi
                </label>
              </FormField>
              <FormField label="">
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.85rem'}}>
                  <input type="checkbox" checked={!!form.allow_negative} onChange={e => f('allow_negative', e.target.checked)} disabled={!isEditing} /> Negatif Stok Izni
                </label>
              </FormField>
              <FormField label="">
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.85rem'}}>
                  <input type="checkbox" checked={!!form.is_weighted} onChange={e => f('is_weighted', e.target.checked)} disabled={!isEditing} /> Tartili Ürün
                </label>
              </FormField>
            </div>
          )}

          {activeTab === 'physical' && (
            <div style={gridStyle}>
              <FormField label="Net Agirlik (kg)">
                <input type="number" step="0.01" value={form.weight_net || ''} onChange={e => f('weight_net', parseFloat(e.target.value) || null)} disabled={!isEditing} />
              </FormField>
              <FormField label="Brut Agirlik (kg)">
                <input type="number" step="0.01" value={form.weight_gross || ''} onChange={e => f('weight_gross', parseFloat(e.target.value) || null)} disabled={!isEditing} />
              </FormField>
              <FormField label="Genislik (cm)">
                <input type="number" step="0.01" value={form.width || ''} onChange={e => f('width', parseFloat(e.target.value) || null)} disabled={!isEditing} />
              </FormField>
              <FormField label="Yükseklik (cm)">
                <input type="number" step="0.01" value={form.height || ''} onChange={e => f('height', parseFloat(e.target.value) || null)} disabled={!isEditing} />
              </FormField>
              <FormField label="Derinlik (cm)">
                <input type="number" step="0.01" value={form.depth || ''} onChange={e => f('depth', parseFloat(e.target.value) || null)} disabled={!isEditing} />
              </FormField>
              <FormField label={<>Renk <button className="quick-add-btn" onClick={()=>setShowQuickAdd('color-mgr')}>+</button></>}>
                <select value={form.color_id||''} onChange={e=>f('color_id',e.target.value||null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {(lookups.colors||[]).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FormField>
              <FormField label={<>Beden <button className="quick-add-btn" onClick={()=>setShowQuickAdd('size-mgr')}>+</button></>}>
                <select value={form.size_id||''} onChange={e=>f('size_id',e.target.value||null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {(lookups.sizes||[]).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </FormField>
              <FormField label={<>Raf <button className="quick-add-btn" onClick={()=>setShowQuickAdd('shelf-mgr')}>+</button></>}>
                <select value={form.shelf_id||''} onChange={e=>f('shelf_id',e.target.value||null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {(lookups.shelves||[]).map(s=><option key={s.id} value={s.id}>{s.code ? `${s.code} - ${s.name}` : s.name}</option>)}
                </select>
              </FormField>
              {STOCK_DEF_FIELDS.map(fd => {
                const opts = (lookups.fieldDefs && lookups.fieldDefs[fd.key]) || [];
                const cur = form[fd.col];
                const inList = !cur || opts.some(o => o.name === cur);
                return (
                  <FormField key={fd.key} label={<>{fd.label} <button className="quick-add-btn" onClick={()=>setShowQuickAdd('fd-'+fd.key)}>+</button></>}>
                    <select value={cur || ''} onChange={e=>f(fd.col, e.target.value)} disabled={!isEditing}>
                      <option value="">Seciniz</option>
                      {!inList && <option value={cur}>{cur}</option>}
                      {opts.map(o=><option key={o.id} value={o.name}>{o.code ? `${o.code} - ${o.name}` : o.name}</option>)}
                    </select>
                  </FormField>
                );
              })}
              <FormField label="Proje">
                <select value={form.project_id||''} onChange={e=>f('project_id',e.target.value||null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {(lookups.projects||[]).map(p=><option key={p.id} value={p.id}>{p.code ? `${p.code} - ${p.name}` : p.name}</option>)}
                </select>
              </FormField>
              <FormField label="Üretim Tarihi">
                <input type="date" value={form.production_date ? String(form.production_date).slice(0,10) : ''} onChange={e => f('production_date', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Yerli Malı">
                <select value={form.is_domestic ? '1' : '0'} onChange={e => f('is_domestic', e.target.value === '1')} disabled={!isEditing}>
                  <option value="0">Hayır</option><option value="1">Evet (Yerli Malı)</option>
                </select>
              </FormField>
              <FormField label="ÖTV Oranı (%)">
                <input type="number" step="0.01" value={form.otv_rate ?? ''} onChange={e => f('otv_rate', e.target.value === '' ? '' : parseFloat(e.target.value))} disabled={!isEditing} />
              </FormField>
              <FormField label="Tevkifat Kodu" hint="KDV tevkifat (örn. 601)">
                <input value={form.withholding_code || ''} onChange={e => f('withholding_code', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Tevkifat Oranı (%)">
                <input type="number" step="0.01" value={form.withholding_rate ?? ''} onChange={e => f('withholding_rate', e.target.value === '' ? '' : parseFloat(e.target.value))} disabled={!isEditing} />
              </FormField>
              <FormField label="Ort. Stok'ta Kalma Süresi (Gün)">
                <input type="number" value={form.avg_shelf_days ?? ''} onChange={e => f('avg_shelf_days', e.target.value)} disabled={!isEditing} />
              </FormField>
            </div>
          )}

          {activeTab === 'movements' && (
            <div>
              {movements.length === 0 ? (
                <EmptyState compact title="Henüz hareket yok" description="Bu stok karta dair henüz bir stok hareketi kaydedilmemiş." />
              ) : (
                <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem',minWidth:1100}}>
                  <thead>
                      <tr style={{borderBottom:'1px solid var(--border-secondary)'}}>
                        <th rowSpan='2' style={{width:32,verticalAlign:'bottom'}}></th>
                        <th rowSpan='2' style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom',whiteSpace:'nowrap'}}>Tarih</th>
                        <th rowSpan='2' style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom',whiteSpace:'nowrap'}}>Tip</th>
                        <th rowSpan='2' style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom',whiteSpace:'nowrap'}}>Belge No</th>
                        <th rowSpan='2' style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom',whiteSpace:'nowrap'}}>Cari</th>
                        <th rowSpan='2' style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom',whiteSpace:'nowrap'}}>Depo</th>
                        <th rowSpan='2' style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom'}}>Açıklama</th>
                        <th rowSpan='2' style={{textAlign:'right',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom',whiteSpace:'nowrap'}}>Giriş</th>
                        <th rowSpan='2' style={{textAlign:'right',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom',whiteSpace:'nowrap'}}>Çıkış</th>
                        <th rowSpan='2' style={{textAlign:'right',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom',whiteSpace:'nowrap',background:'rgba(99,102,241,0.04)'}}>Bakiye</th>
                        <th colSpan='2' style={{textAlign:'center',padding:'4px 8px',color:'var(--accent-primary)',fontWeight:600,borderBottom:'1px solid var(--border-secondary)',borderLeft:'1px solid var(--border-secondary)',fontSize:'0.75rem',whiteSpace:'nowrap'}}>YEREL PARA BIRIMI</th>
                        <th colSpan='3' style={{textAlign:'center',padding:'4px 8px',color:'#3b82f6',fontWeight:600,borderBottom:'1px solid var(--border-secondary)',borderLeft:'1px solid var(--border-secondary)',fontSize:'0.75rem',whiteSpace:'nowrap'}}>ISLEM DOVIZI</th>
                      </tr>
                      <tr style={{borderBottom:'2px solid var(--border-secondary)'}}>
                        <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,borderLeft:'1px solid var(--border-secondary)',fontSize:'0.75rem',whiteSpace:'nowrap'}}>Tutar</th>
                        <th style={{textAlign:'center',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>PB</th>
                        <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,borderLeft:'1px solid var(--border-secondary)',fontSize:'0.75rem',whiteSpace:'nowrap'}}>Tutar</th>
                        <th style={{textAlign:'center',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>PB</th>
                        <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem',whiteSpace:'nowrap'}}>Kur</th>
                      </tr>
                    </thead>
                  <tbody>{(() => {
                    let runningBalance = 0;
                    return movements.map(m => {
                      const qty = parseFloat(m.quantity) || 0;
                      runningBalance += qty;
                      const isLocal = !m.foreign_currency || m.foreign_currency === m.currency_code;
                      const localTotal = Math.abs(m.total_price||0);
                      const foreignTotal = isLocal ? 0 : (m.foreign_total ? Math.abs(m.foreign_total) : Math.abs(m.total_price||0));
                      const isIn  = qty > 0;
                      const snap  = runningBalance;
                      const openLinkedDoc = () => {
                        if (!m.source_id || !onNavigate) return;
                        const src = m.source_type;
                        const mt  = m.movement_type || '';
                        let targetPage = null;
                        if (src === 'sales_invoice')   targetPage = mt.includes('return') ? 'sales-returns' : 'sales';
                        else if (src === 'purchase_invoice') targetPage = mt.includes('return') ? 'purchases-returns' : 'purchases';
                        else if (src === 'sales_waybill')    targetPage = 'waybills-sales';
                        else if (src === 'purchase_waybill') targetPage = 'waybills-purchase';
                        else if (src === 'warehouse_transfer') targetPage = 'warehouse-transfers';
                        else if (src === 'stock_op_voucher') {
                          // Hareketin tipine göre yönlendirme
                          if (mt === 'count_in')  targetPage = 'stock-count-surplus';
                          else if (mt === 'count_out') targetPage = 'stock-count-deficit';
                          else if (mt === 'waste_out') targetPage = 'stock-waste';
                        }
                        if (targetPage) { setShowModal(false); onNavigate(targetPage, m.source_id); }
                        else alert('Bu kaynak tipi için yönlendirme tanımlı değil: ' + src);
                      };
                      return (
                        <tr key={m.id} style={{borderBottom:'1px solid var(--border-secondary)'}}>
                          <td style={{padding:'4px 6px',textAlign:'center'}}>
                            {m.source_id && (
                              <button onClick={openLinkedDoc} title="Bağlı belgeyi aç"
                                style={{background:'transparent',border:'1px solid var(--border-secondary)',borderRadius:6,padding:'3px 6px',cursor:'pointer',color:'var(--accent-primary)'}}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                              </button>
                            )}
                          </td>
                          <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{new Date(m.date).toLocaleDateString('tr-TR')}</td>
                          <td style={{padding:'6px 8px'}}>
                            <span style={{fontSize:'0.7rem',padding:'2px 7px',borderRadius:4,background:isIn?'rgba(16,185,129,0.1)':'rgba(239,68,68,0.1)',color:isIn?'var(--success)':'var(--danger)',whiteSpace:'nowrap'}}>
                              {(() => {
                                const map = {
                                  sales_out:                   'Satış Faturası',
                                  sales_return_in:             'Satış İade Faturası',
                                  retail_sale_out:             'Perakende Satış Faturası',
                                  retail_sale_return_in:       'Perakende Satış İade Faturası',
                                  purchase_in:                 'Alış Faturası',
                                  purchase_return_out:         'Alış İade Faturası',
                                  sales_waybill_out:           'Satış İrsaliyesi',
                                  sales_waybill_return_in:     'Satış İade İrsaliyesi',
                                  purchase_waybill_in:         'Alış İrsaliyesi',
                                  purchase_waybill_return_out: 'Alış İade İrsaliyesi',
                                  waybill_out:                 'İrsaliye (eski)',
                                  waybill_return_in:           'İrsaliye İade (eski)',
                                  transfer_in:                 'Transfer Giriş',
                                  transfer_out:                'Transfer Çıkış',
                                  adjustment:                  'Düzeltme',
                                  waste:                       'Fire',
                                  count_in:                    'Sayım Fazlası Fişi',
                                  count_out:                   'Sayım Eksiği Fişi',
                                  waste_out:                   'Fire Fişi',
                                  branch_in:                   'Bayi Depo Girişi',
                                  branch_out:                  'Bayi Depo Çıkışı',
                                };
                                return map[m.movement_type] || (m.movement_type || '-');
                              })()}
                            </span>
                          </td>
                          <td style={{padding:'6px 8px',color:'var(--text-secondary)',fontFamily:'monospace',fontSize:'0.76rem',whiteSpace:'nowrap'}}>{m.doc_number||'-'}</td>
                          <td style={{padding:'6px 8px',fontSize:'0.8rem',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={m.account_name||''}>{m.account_name||'-'}</td>
                          <td style={{padding:'6px 8px',fontSize:'0.8rem',whiteSpace:'nowrap',color:'var(--accent-primary)'}}>{m.warehouse_name||'-'}</td>
                          <td style={{padding:'6px 8px',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.description||'-'}</td>
                          <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:'var(--success)',whiteSpace:'nowrap'}}>
                            {isIn ? fmtQty(qty) : '-'}
                          </td>
                          <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:'var(--danger)',whiteSpace:'nowrap'}}>
                            {!isIn ? fmtQty(Math.abs(qty)) : '-'}
                          </td>
                          <td style={{padding:'6px 8px',textAlign:'right',fontWeight:700,whiteSpace:'nowrap',background:'rgba(99,102,241,0.04)',color:snap>=0?'var(--accent-primary)':'var(--danger)'}}>
                            {fmtQty(snap)}
                          </td>
                          <td style={{padding:'6px 8px',textAlign:'right',whiteSpace:'nowrap',borderLeft:'1px solid var(--border-secondary)'}}>
                            {fmtAmount(localTotal)}
                          </td>
                          <td style={{padding:'6px 8px',textAlign:'center',fontSize:'0.72rem',color:'var(--text-tertiary)'}}>TRY</td>
                          <td style={{padding:'6px 8px',textAlign:'right',whiteSpace:'nowrap',borderLeft:'1px solid var(--border-secondary)',color:(m.foreign_currency&&m.foreign_currency!==m.currency_code)?'var(--text-primary)':'var(--text-tertiary)'}}>
                            {isLocal ? '-' : fmtAmount(m.foreign_total||foreignTotal)}
                          </td>
                          <td style={{padding:'6px 8px',textAlign:'center',fontSize:'0.75rem',color:(m.foreign_currency&&m.foreign_currency!==m.currency_code)?'var(--accent-primary)':'var(--text-tertiary)'}}>
                            {isLocal ? '-' : (m.foreign_currency||m.currency_code)}
                          </td>
                          <td style={{padding:'6px 8px',textAlign:'right',fontSize:'0.75rem',fontFamily:'monospace',color:isLocal?'var(--text-tertiary)':'var(--text-secondary)',whiteSpace:'nowrap'}}>
                            {isLocal ? '-' : fmtRate(m.exchange_rate||1)}
                          </td>
                        </tr>
                      );
                    });
                  })()}</tbody>
                </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'image' && (
            <div style={{padding:'16px 0'}}>
              {isNew ? (
                <EmptyState compact title="Önce kaydedin" description="Stoku kaydettikten sonra resim yükleyebilirsiniz (en fazla 4 resim)." />
              ) : (
                <>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12 }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      {images.length} / 4 resim — yıldıza tıklayarak ana resmi belirleyin
                    </div>
                    {images.length < 4 && (
                      <Button variant="ghost" size="sm" onClick={() => imgInputRef.current?.click()} disabled={imgUploading} loading={imgUploading}>
                        {imgUploading ? 'Yükleniyor...' : '+ Resim Ekle'}
                      </Button>
                    )}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                    {[0,1,2,3].map(slotIdx => {
                      const img = images[slotIdx];
                      if (!img) {
                        return (
                          <div key={'slot-'+slotIdx}
                            onClick={() => images.length < 4 && imgInputRef.current?.click()}
                            style={{
                              border:'2px dashed var(--border-secondary)', borderRadius: 8, height: 180,
                              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                              gap: 6, cursor: images.length < 4 ? 'pointer' : 'default',
                              color:'var(--text-tertiary)', background:'var(--bg-secondary)',
                              opacity: images.length < 4 ? 1 : 0.5,
                            }}
                          >
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                            <span style={{ fontSize: '0.75rem' }}>{slotIdx === 0 ? 'Ana resim' : 'Resim ' + (slotIdx + 1)}</span>
                          </div>
                        );
                      }
                      return (
                        <div key={img.id}
                          style={{
                            position:'relative', border:'1px solid var(--border-secondary)', borderRadius: 8,
                            height: 180, overflow:'hidden', background:'var(--bg-secondary)',
                          }}
                        >
                          <img src={IMG_BASE + img.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                          {/* Primary star */}
                          <button
                            type="button"
                            onClick={() => !img.is_primary && handleImageSetPrimary(img.id)}
                            title={img.is_primary ? 'Ana resim' : 'Ana resim yap'}
                            style={{
                              position:'absolute', top: 6, left: 6, width: 28, height: 28, borderRadius: '50%',
                              border:'none', background: img.is_primary ? 'rgba(255,193,7,0.95)' : 'rgba(0,0,0,0.45)',
                              color: img.is_primary ? '#000' : '#fff', cursor: img.is_primary ? 'default' : 'pointer',
                              display:'flex', alignItems:'center', justifyContent:'center', fontSize: '1rem',
                            }}
                          >★</button>
                          {/* Delete */}
                          <button
                            type="button"
                            onClick={() => handleImageDelete(img.id)}
                            title="Sil"
                            style={{
                              position:'absolute', top: 6, right: 6, width: 28, height: 28, borderRadius:'50%',
                              border:'none', background:'rgba(220,53,69,0.9)', color:'#fff', cursor:'pointer',
                              fontWeight:700,
                            }}
                          >✕</button>
                          {img.is_primary && (
                            <div style={{
                              position:'absolute', bottom: 0, left: 0, right: 0, padding:'4px 8px',
                              background:'rgba(0,0,0,0.55)', color:'#fff', fontSize:'0.7rem', textAlign:'center'
                            }}>Ana resim</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              <input ref={imgInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload} />
            </div>
          )}

          {activeTab === 'custom' && (
            selected?.id ? (
              <CustomFieldsSection
                entityType="stock"
                entityId={selected.id}
                autoSave
                disabled={!isEditing}
              />
            ) : (
              <div style={{ padding: 20, color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                Özel alanları doldurmak için önce stok kaydını oluşturun.
              </div>
            )
          )}

          {activeTab === 'documents' && (
            <AttachmentManager entityType="stock" entityId={selected?.id} />
          )}
          {activeTab === 'notes' && (
            <FormField label="Notlar" fullWidth>
              <textarea rows="6" value={form.notes || ''} onChange={e => f('notes', e.target.value)} disabled={!isEditing} />
            </FormField>
          )}
        </Modal>
      )}

      {(() => {
        const map = {
          'group-mgr':    { title:'Stok Grubu', items:lookups.groups||[],     apiBase:'/stocks/groups' },
          'shelf-mgr':    { title:'Raf',        items:lookups.shelves||[],    apiBase:'/stocks/shelves' },
          'category-mgr': { title:'Kategori',   items:lookups.categories||[], apiBase:'/stocks/categories' },
          'brand-mgr':    { title:'Marka',      items:lookups.brands||[],     apiBase:'/stocks/brands' },
          'model-mgr':    { title:'Model',      items:lookups.models||[],     apiBase:'/stocks/models' },
          'unit-mgr':     { title:'Birim',      items:lookups.units||[],      apiBase:'/stocks/units' },
          'color-mgr':    { title:'Renk',       items:lookups.colors||[],     apiBase:'/colors' },
          'size-mgr':     { title:'Beden',      items:lookups.sizes||[],      apiBase:'/sizes' },
        };
        // Generic tanımlı alanlar (fd-season, fd-gtip, ...)
        STOCK_DEF_FIELDS.forEach(fd => {
          map['fd-'+fd.key] = { title: fd.label, items: (lookups.fieldDefs && lookups.fieldDefs[fd.key]) || [], apiBase: '/stocks/field-defs/'+fd.key };
        });
        const cfg = map[showQuickAdd];
        return cfg ? (
          <QuickListManager open={true} title={cfg.title} items={cfg.items}
            apiBase={cfg.apiBase} onClose={() => setShowQuickAdd(null)} onChanged={fetchLookups} />
        ) : null;
      })()}

      {/* ANALİZ MODAL */}
      {analysisStock && (
        <Modal
          icon={TAB_ICONS.activity}
          title={`${analysisStock.code} - ${analysisStock.name} — Stok Analizi`}
          subtitle={
            <>
              <code style={{fontFamily:'JetBrains Mono,monospace',fontSize:'var(--text-xs)',background:'var(--bg-secondary)',padding:'1px 6px',borderRadius:4}}>{analysisStock.code}</code>
              <span> · Stok hareketleri ve depo dağılımı</span>
            </>
          }
          size="lg"
          onClose={() => setAnalysisStock(null)}
        >
          {analysisLoading ? (
            <div style={{textAlign:'center',padding:40,color:'var(--text-tertiary)'}}>Yükleniyor...</div>
          ) : !analysisData ? (
            <EmptyState compact title="Veri alınamadı" />
          ) : (()=>{
            const fmt = n => fmtQty(n);
            const fmtAmt = n => fmtAmount(n);
            const {totals,warehouses} = analysisData;
            return (
              <div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:10,marginBottom:24}}>
                  {[
                    {label:'Mevcut Stok',value:fmt(totals.total_qty),color:'var(--accent-primary)'},
                    {label:'🔒 Rezerv',value:fmt(totals.reserved_qty || 0),color:'#b45309'},
                    {label:'✓ Kullanılabilir',value:fmt((totals.available_qty != null) ? totals.available_qty : (totals.total_qty - (totals.reserved_qty || 0))),color:'var(--success)'},
                    {label:'Giren Mkt',value:fmt(totals.total_in_qty),color:'var(--success)'},
                    {label:'Cikan Mkt',value:fmt(totals.total_out_qty),color:'var(--danger)'},
                    {label:'Giren Tutar',value:fmtAmt(totals.total_in_amt),color:'var(--success)'},
                    {label:'Cikan Tutar',value:fmtAmt(totals.total_out_amt),color:'var(--danger)'},
                  ].map(c=>(
                    <div key={c.label} style={{background:'var(--bg-secondary)',borderRadius:10,padding:'12px 14px',border:'1px solid var(--border-secondary)'}}>
                      <div style={{fontSize:'0.72rem',color:'var(--text-tertiary)',marginBottom:4}}>{c.label}</div>
                      <div style={{fontSize:'1.0rem',fontWeight:700,color:c.color}}>{c.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:'0.75rem',color:'var(--text-tertiary)',marginBottom:16,padding:'6px 10px',background:'var(--bg-secondary)',borderRadius:6,border:'1px solid var(--border-secondary)'}}>
                  * Tutarlar <strong>KDV Haric</strong> yerel para birimi cinsinden gosterilmektedir.
                </div>
                <div style={{fontSize:'0.78rem',fontWeight:600,color:'var(--text-tertiary)',marginBottom:8,letterSpacing:'0.05em'}}>DEPO BAZLI DETAY</div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.83rem'}}>
                  <thead>
                    <tr style={{borderBottom:'2px solid var(--border-secondary)'}}>
                      <th style={{textAlign:'left',padding:'7px 8px',color:'var(--text-tertiary)',fontWeight:500}}>Depo</th>
                      <th style={{textAlign:'right',padding:'7px 8px',color:'var(--text-tertiary)',fontWeight:500}}>Giren</th>
                      <th style={{textAlign:'right',padding:'7px 8px',color:'var(--text-tertiary)',fontWeight:500}}>Cikan</th>
                      <th style={{textAlign:'right',padding:'7px 8px',color:'var(--text-tertiary)',fontWeight:500}}>Mevcut</th>
                      <th style={{textAlign:'right',padding:'7px 8px',color:'var(--text-tertiary)',fontWeight:500}}>🔒 Rezerv</th>
                      <th style={{textAlign:'right',padding:'7px 8px',color:'var(--text-tertiary)',fontWeight:500}}>✓ Kullanılabilir</th>
                      <th style={{textAlign:'right',padding:'7px 8px',color:'var(--text-tertiary)',fontWeight:500}}>Giren Tutar</th>
                      <th style={{textAlign:'right',padding:'7px 8px',color:'var(--text-tertiary)',fontWeight:500}}>Cikan Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warehouses.length===0 ? (
                      <tr><td colSpan={8} style={{padding:16,textAlign:'center',color:'var(--text-tertiary)'}}>Stok hareketi bulunamadı</td></tr>
                    ) : warehouses.map((w,i)=>{
                      const reserved = parseFloat(w.reserved_qty)||0;
                      const available = (w.available_qty != null) ? parseFloat(w.available_qty) : ((parseFloat(w.current_qty)||0) - reserved);
                      return (
                        <tr key={i} style={{borderBottom:'1px solid var(--border-secondary)'}}>
                          <td style={{padding:'7px 8px',fontWeight:500}}>{w.warehouse_name||'-'}</td>
                          <td style={{padding:'7px 8px',textAlign:'right',color:'var(--success)'}}>{fmt(w.total_in_qty)}</td>
                          <td style={{padding:'7px 8px',textAlign:'right',color:'var(--danger)'}}>{fmt(w.total_out_qty)}</td>
                          <td style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:'var(--accent-primary)'}}>{fmt(w.current_qty)}</td>
                          <td style={{padding:'7px 8px',textAlign:'right',color: reserved > 0 ? '#b45309' : 'var(--text-tertiary)', fontWeight: reserved > 0 ? 600 : 400}}>{reserved > 0 ? fmt(reserved) : '—'}</td>
                          <td style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color: available <= 0 ? 'var(--danger)' : 'var(--success)'}}>{fmt(available)}</td>
                          <td style={{padding:'7px 8px',textAlign:'right',color:'var(--success)'}}>{fmtAmt(w.total_in_amt)}</td>
                          <td style={{padding:'7px 8px',textAlign:'right',color:'var(--danger)'}}>{fmtAmt(w.total_out_amt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </Modal>
      )}

      <BarcodeScanner
        open={barcodeScannerOpen}
        onDetected={(code) => { f(barcodeScannerTarget, code); setBarcodeScannerOpen(false); }}
        onClose={() => setBarcodeScannerOpen(false)}
      />

      {bulkPriceModal && (
        <BulkPriceModal
          stockCount={bulkPriceModal.ids.length}
          priceDefs={priceDefs}
          onClose={() => setBulkPriceModal(null)}
          onSubmit={async (payload) => {
            try {
              const r = await api.post('/stocks/bulk', { action: 'set_prices', ids: bulkPriceModal.ids, value: payload });
              if (r.data.success) {
                const d = r.data.data;
                bulkPriceModal.clearFn && bulkPriceModal.clearFn();
                setBulkPriceModal(null);
                fetchStocks();
                alert(`Fiyat güncellendi — ${d.updated || 0} satır güncellendi, ${d.inserted || 0} yeni satır oluşturuldu, ${d.history || 0} değişiklik kaydedildi.`);
              }
            } catch (e) { alert(e.response?.data?.message || e.message); }
          }}
        />
      )}
    </div>
  );
}

// ─── Toplu Fiyat Güncelleme Modalı ───────────────────────────────────
function BulkPriceModal({ stockCount, priceDefs, onClose, onSubmit }) {
  const OPERATIONS = [
    { value: 'set',     label: 'Direkt değer ata (üstüne yaz)' },
    { value: 'inc_pct', label: '% oranında ZAM yap' },
    { value: 'dec_pct', label: '% oranında İNDİRİM yap' },
    { value: 'inc_amt', label: 'Sabit tutar EKLE' },
    { value: 'dec_amt', label: 'Sabit tutar ÇIKAR' },
  ];
  const CURRENCIES = ['TRY','USD','EUR','GBP','CHF','SAR','AED'];

  const [operation, setOperation] = useState('set');
  const [amount, setAmount] = useState('');
  // Her price definition için: { selectedCols: Set('price1'...), currency, vatIncluded }
  const [rows, setRows] = useState(() => {
    const map = {};
    (priceDefs || []).forEach(d => {
      map[d.id] = {
        selectedCols: new Set(),
        currency: d.currency_code || 'TRY',
        vatIncluded: !!d.vat_included,
      };
    });
    return map;
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleCell = (defId, col) => {
    setRows(prev => {
      const r = prev[defId];
      const next = new Set(r.selectedCols);
      if (next.has(col)) next.delete(col); else next.add(col);
      return { ...prev, [defId]: { ...r, selectedCols: next } };
    });
  };
  const setRowField = (defId, field, value) => {
    setRows(prev => ({ ...prev, [defId]: { ...prev[defId], [field]: value } }));
  };

  const totalCells = Object.values(rows).reduce((s, r) => s + r.selectedCols.size, 0);

  const submit = async () => {
    if (totalCells === 0) { alert('En az bir hücre seçmelisiniz (Fiyat 1/2/3 sütunlarındaki kutucukları işaretleyin)'); return; }
    const a = parseFloat(amount);
    if (isNaN(a) || a < 0) { alert('Geçerli bir tutar/oran girin'); return; }

    const changes = [];
    for (const def of (priceDefs || [])) {
      const r = rows[def.id];
      if (!r) continue;
      for (const col of r.selectedCols) {
        changes.push({
          price_definition_id: def.id,
          target_price: col,
          currency_code: r.currency,
          vat_included: r.vatIncluded,
        });
      }
    }

    setSubmitting(true);
    try {
      await onSubmit({ operation, amount: a, changes });
    } finally {
      setSubmitting(false);
    }
  };

  const opLabel = OPERATIONS.find(o => o.value === operation)?.label || operation;
  const valueLabel = operation === 'set' ? 'Yeni Fiyat (her hücreye uygulanacak)'
                  : operation === 'inc_pct' || operation === 'dec_pct' ? 'Oran (%)'
                  : 'Tutar';

  return (
    <Modal
      title="Toplu Fiyat Güncelleme"
      subtitle={`${stockCount} stok seçili · Stok kartındaki fiyat tablosunun aynısı`}
      size="lg"
      onClose={() => !submitting && onClose()}
      closeOnOverlay={false}
      primaryAction={{ label: 'Fiyatları Güncelle', onClick: submit, loading: submitting }}
      cancelAction={{ label: 'Vazgeç', onClick: onClose, disabled: submitting }}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        {/* İşlem türü + tutar */}
        <div style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) minmax(140px, 1fr)', gap: 12 }}>
            <FormField label="İşlem Türü">
              <select value={operation} onChange={e => setOperation(e.target.value)}>
                {OPERATIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FormField>
            <FormField label={valueLabel + ' *'}>
              <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" autoFocus />
            </FormField>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 6 }}>
            💡 İşaretlediğin her hücreye "<strong>{opLabel}</strong>" işlemi uygulanır. Önceki/yeni fiyat farkı geçmişe kaydedilir.
          </div>
        </div>

        {/* Fiyat tablosu */}
        <div style={{ overflow: 'auto' }}>
          {priceDefs.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>
              Aktif fiyat tanımı bulunamadı. Ayarlar → Fiyat Tanımları menüsünden ekleyin.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Fiyat Tanımı</th>
                  <th style={{ textAlign: 'center', padding: '8px 6px', width: 90 }}>Fiyat 1</th>
                  <th style={{ textAlign: 'center', padding: '8px 6px', width: 90 }}>Fiyat 2</th>
                  <th style={{ textAlign: 'center', padding: '8px 6px', width: 90 }}>Fiyat 3</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px', width: 100 }}>Para Birimi</th>
                  <th style={{ textAlign: 'center', padding: '8px 6px', width: 70 }}>KDV Dahil</th>
                </tr>
              </thead>
              <tbody>
                {priceDefs.map(def => {
                  const r = rows[def.id];
                  if (!r) return null;
                  const isAnySelected = r.selectedCols.size > 0;
                  return (
                    <tr key={def.id} style={{
                      borderBottom: '1px solid var(--border-secondary)',
                      background: isAnySelected ? 'rgba(59,130,246,0.05)' : undefined,
                    }}>
                      <td style={{ padding: '8px 6px' }}>
                        <div style={{ fontWeight: 600 }}>{def.name}</div>
                        {def.code && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{def.code}</div>}
                      </td>
                      {['price1', 'price2', 'price3'].map(col => (
                        <td key={col} style={{ textAlign: 'center', padding: '8px 6px' }}>
                          <input type="checkbox"
                            checked={r.selectedCols.has(col)}
                            onChange={() => toggleCell(def.id, col)}
                            style={{ cursor: 'pointer', width: 18, height: 18 }}
                            title={`${def.name} - ${col}`} />
                        </td>
                      ))}
                      <td style={{ padding: '8px 6px' }}>
                        <select value={r.currency} onChange={e => setRowField(def.id, 'currency', e.target.value)}
                          style={{ width: '100%', fontSize: '0.82rem' }}>
                          {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 6px' }}>
                        <input type="checkbox" checked={r.vatIncluded}
                          onChange={e => setRowField(def.id, 'vatIncluded', e.target.checked)}
                          style={{ cursor: 'pointer', width: 18, height: 18 }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ padding: '10px 6px', textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    Toplam seçili hücre: <strong style={{ color: 'var(--accent-primary)' }}>{totalCells}</strong>
                    {totalCells > 0 && ` · ${stockCount} stoğun her birinde ${totalCells} fiyat güncellenecek = ${totalCells * stockCount} değişiklik`}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <div style={{ padding: '8px 12px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 6, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          ℹ <strong>Para birimi ve KDV</strong> ayarları, sadece stokta o fiyat tanımı henüz yoksa <em>yeni satır oluşturulurken</em> kullanılır. Mevcut satırlarda dokunulmaz, sadece fiyat alanları güncellenir.
        </div>
      </div>
    </Modal>
  );
}

export default StocksPage;
